import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Accounts Tree
export const getAccountsTree = createAsyncThunk(
  "accountTree/getAccountsTree",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/accounts/tree`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get accounts tree!";
      return rejectWithValue(message);
    }
  },
);

// Get Posting Accounts
export const getPostingAccounts = createAsyncThunk(
  "postingAccount/getPostingAccounts",
  async (arg = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }

      let params = {};
      if (typeof arg === "string") {
        params.search = arg;
      } else {
        params = { ...arg };
      }

      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append("search", params.search);
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);

      const url = `${BASE_URL}/accounts/posting${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get posting accounts!";
      return rejectWithValue(message);
    }
  },
);

// Get Account Links
export const getAccountLinks = createAsyncThunk(
  "accountLinks/getAccountLinks",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/account-links`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get account links!";
      return rejectWithValue(message);
    }
  },
);

// Get Unlinked Accounts
export const getUnlinkedAccounts = createAsyncThunk(
  "unLinkedAccount/getUnlinkedAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/accounts/unlinked`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get unlinked accounts!";
      return rejectWithValue(message);
    }
  },
);

// Update Account Link
export const updateAccountLink = createAsyncThunk(
  "AccountLink/updateAccountLink",
  async (linkData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(`${BASE_URL}/account-links`, linkData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update account link!";
      return rejectWithValue(message);
    }
  },
);

// Get Account Details
export const getAccountDetails = createAsyncThunk(
  "account/getAccountDetails",
  async (accountId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/accounts/${accountId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get account details!";
      return rejectWithValue(message);
    }
  },
);

// Toggle Account Status
export const toggleAccountStatus = createAsyncThunk(
  "account/toggleAccountStatus",
  async (accountId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.patch(
        `${BASE_URL}/accounts/${accountId}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle account status!";
      return rejectWithValue(message);
    }
  },
);

// Add Account
export const addAccount = createAsyncThunk(
  "account/addAccount",
  async (accountData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(`${BASE_URL}/accounts`, accountData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      // Handle different error response formats
      let message = "Failed to add account!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// Update Account
export const updateAccount = createAsyncThunk(
  "account/updateAccount",
  async ({ accountId, accountData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.put(
        `${BASE_URL}/accounts/${accountId}`,
        accountData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle different error response formats
      let message = "Failed to update account!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// Delete Account
export const deleteAccount = createAsyncThunk(
  "account/deleteAccount",
  async (accountId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.delete(`${BASE_URL}/accounts/${accountId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete account!";
      return rejectWithValue(message);
    }
  },
);
